import { IMPERSONATION_READ_ONLY, IMPERSONATION_READ_ONLY_MESSAGE } from "@mfa-aff/shared";
import { notify } from "@mfa-aff/ui";
import { mockAdminViewActive } from "../mocks/admin-view";
import { API_BASE, MOCK_DELAY_MS, USE_MOCKS } from "./config";
import { firstReadOnlyNotice, impersonationAdminUrl } from "./impersonation-state";
import { reportPanelClosed, reportRulesRequired } from "./panel-signal";
import { partnerPaths } from "./paths";

/**
 * Wire conventions (apps/api README): money is rial BigInt serialized as a JSON **string**
 * (`RialString`), rates are basis points (number), dates are ISO strings. Responses are never
 * converted to numbers here; display goes through `<Money rial="…">` and the shared helpers.
 */
export type RialString = string;
export type IsoDate = string;

export class ApiError extends Error {
    constructor(
        readonly status: number,
        message: string,
        /** Field the server complained about (UnprocessableEntity `{ field }`), when any. */
        readonly field?: string,
        /** Machine-readable reason (`{ code }`), e.g. `rules_acceptance_required`. */
        readonly code?: string,
    ) {
        super(message);
        this.name = "ApiError";
    }
}

/** JSON.stringify that sends bigint as a digit string (the API's money format). */
export function toJson(body: unknown): string {
    return JSON.stringify(body, (_k, v) => (typeof v === "bigint" ? v.toString() : v));
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/**
 * A sensitive change (Sheba, ref code, notification mobile, payout) needs a WHMCS sign-in from the
 * last 15 minutes (apps/api partner-session.ts, review A5): the API answers 401 `reauth_required`
 * and the panel goes through «ورود با حساب میزبان‌فا» again, back to the same page.
 */
export function startReauth(): void {
    const next = `${window.location.pathname}${window.location.search}`;
    window.location.assign(`${API_BASE}${partnerPaths.oidcStart}?reauth=1&next=${encodeURIComponent(next)}`);
}

/** Public panel routes: a 401 there is an answer, not a reason to go to /login. */
const PUBLIC_ROUTES = ["/login", "/join", "/auth/", "/program"];

/**
 * Double-submit CSRF for partner writes (apps/api partner-auth/partner-session.ts): echo the
 * readable `__Host-mzp_csrf` cookie in `X-Partner-CSRF`. Absent before sign-in (login routes skip it).
 * An admin's read-only view has its own token (`__Host-mzp_imp_csrf`), used first: the API
 * prefers the view over the admin's own partner session too.
 */
function csrfHeader(): Record<string, string> {
    if (typeof document === "undefined") return {};
    const cookies = document.cookie.split("; ");
    for (const name of ["__Host-mzp_imp_csrf", "__Host-mzp_csrf"]) {
        const match = cookies.find((c) => c.startsWith(`${name}=`));
        if (match) return { "X-Partner-CSRF": decodeURIComponent(match.slice(name.length + 1)) };
    }
    return {};
}

/** One friendly toast for writes refused during an admin's read-only view (never a logout or re-sign-in). */
function readOnlyError(): ApiError {
    if (firstReadOnlyNotice()) notify.info("فقط مشاهده", { description: IMPERSONATION_READ_ONLY_MESSAGE });
    return new ApiError(403, IMPERSONATION_READ_ONLY_MESSAGE, undefined, IMPERSONATION_READ_ONLY);
}

export async function apiFetch<T>(method: Method, path: string, body?: unknown): Promise<T> {
    const isForm = typeof FormData !== "undefined" && body instanceof FormData;
    const res = await fetch(`${API_BASE}${path}`, {
        method,
        credentials: "include",
        headers: {
            Accept: "application/json",
            ...(body !== undefined && !isForm ? { "Content-Type": "application/json" } : {}),
            ...(method !== "GET" ? csrfHeader() : {}),
        },
        body: body === undefined ? undefined : isForm ? (body as FormData) : toJson(body),
    });
    const text = await res.text();
    let data: unknown;
    try {
        data = text ? (JSON.parse(text) as unknown) : undefined;
    } catch {
        data = undefined;
    }
    if (!res.ok) {
        const err = (data ?? {}) as { message?: string | string[]; field?: string; error?: string; code?: string };
        const message = Array.isArray(err.message) ? err.message.join("، ") : (err.message ?? "درخواست انجام نشد.");
        // The owner closed the panel (apps/api partner-panel): the whole app switches to the closed screen.
        if (res.status === 503 && err.error === "partner_panel_closed") reportPanelClosed(message);
        // A new rule book is in force and not accepted yet (apps/api rules-gate.ts): the layout opens the accept dialog.
        if (res.status === 403 && err.code === "rules_acceptance_required") reportRulesRequired();
        // An admin's read-only view refused a write (apps/api impersonation/).
        if (res.status === 403 && err.error === IMPERSONATION_READ_ONLY) throw readOnlyError();
        // This tab was an admin's view and it has ended: back to the admin panel, never a partner sign-in.
        const adminUrl = impersonationAdminUrl();
        if (res.status === 401 && adminUrl && typeof window !== "undefined") {
            window.location.assign(adminUrl);
        } else if (res.status === 401 && err.error === "reauth_required" && typeof window !== "undefined") {
            startReauth();
        } else if (res.status === 401 && typeof window !== "undefined" && !PUBLIC_ROUTES.some((r) => window.location.pathname.startsWith(r))) {
            window.location.assign("/login");
        }
        throw new ApiError(res.status, message, err.field, err.code);
    }
    // An empty 200 body is a JSON null on the wire (e.g. no payout rule yet); React Query refuses undefined.
    return (data ?? null) as T;
}

const delay = <T>(value: T | (() => T | Promise<T>)) =>
    new Promise<T>((resolve, reject) =>
        window.setTimeout(async () => {
            try {
                resolve(typeof value === "function" ? await (value as () => T | Promise<T>)() : value);
            } catch (e) {
                reject(e);
            }
        }, MOCK_DELAY_MS),
    );

/** GET `path` from the API, or from `mock` when mocks are on. Use inside `queryFn`. */
export function get<T>(path: string, mock: () => T | Promise<T>): Promise<T> {
    return USE_MOCKS ? delay(mock) : apiFetch<T>("GET", path);
}

/**
 * POST/PUT `path` with `body`, or apply `mock` (which may mutate the in-memory mock state). The mock
 * admin view (`?mock_impersonation=1`) refuses writes like the API does, except «برگشت به پنل مدیریت».
 */
export function send<T>(method: Exclude<Method, "GET">, path: string, body: unknown, mock: () => T | Promise<T>): Promise<T> {
    if (USE_MOCKS && path !== partnerPaths.impersonateStop && mockAdminViewActive()) return delay(() => Promise.reject(readOnlyError()));
    return USE_MOCKS ? delay(mock) : apiFetch<T>(method, path, body);
}

/** Query-string helper that drops empty values. */
export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
    const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "");
    return entries.length ? `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()}` : "";
}

/** Random client idempotency key (payouts, promo creation): ^[A-Za-z0-9_-]{8,64}$. */
export function idempotencyKey(): string {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}
